import React from 'react';
import { Pressable, StyleSheet, ViewStyle, StyleProp, ActivityIndicator, TextStyle } from 'react-native';
import { Theme } from '../../utils/theme';
import Text from './Text';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'link';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  style?: StyleProp<ViewStyle>;
  icon?: string;
}

export default function Button({
  title,
  onPress,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = true,
  style,
  icon,
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <Pressable
      style={({ pressed }) => [
        styles.button,
        styles[`button${variant.charAt(0).toUpperCase() + variant.slice(1)}` as keyof typeof styles],
        size !== 'medium' && styles[`button${size.charAt(0).toUpperCase() + size.slice(1)}` as keyof typeof styles],
        !fullWidth && styles.buttonNotFullWidth,
        pressed && !isDisabled && styles.buttonPressed,
        isDisabled && styles.buttonDisabled, // Apply disabled last to override everything
        style,
      ]}
      onPress={onPress}
      disabled={isDisabled}
    >
      {loading ? (
        <ActivityIndicator 
          color={variant === 'primary' ? Theme.colors.textInverse : Theme.colors.primary} 
        />
      ) : (
        <>
          {icon && (
            <Text 
              style={[
                styles.text,
                styles[`text${variant.charAt(0).toUpperCase() + variant.slice(1)}` as keyof typeof styles],
                size !== 'medium' && styles[`text${size.charAt(0).toUpperCase() + size.slice(1)}` as keyof typeof styles],
                styles.icon
              ]}
            >
              {icon}
            </Text>
          )}
          <Text 
            style={[
              styles.text,
              styles[`text${variant.charAt(0).toUpperCase() + variant.slice(1)}` as keyof typeof styles],
              size !== 'medium' && styles[`text${size.charAt(0).toUpperCase() + size.slice(1)}` as keyof typeof styles],
            ]}
          >
            {title}
          </Text>
        </>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    borderRadius: Theme.borderRadius.md,
    paddingVertical: Theme.spacing.lg,
    paddingHorizontal: Theme.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    minHeight: 56,
    gap: Theme.spacing.sm,
  },
  buttonNotFullWidth: {
    alignSelf: 'flex-start',
    minWidth: 120,
  },
  
  // Variants
  buttonPrimary: {
    backgroundColor: Theme.colors.primary,
  },
  buttonSecondary: {
    backgroundColor: Theme.colors.surface,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  buttonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: Theme.colors.primary,
  },
  buttonLink: {
    backgroundColor: 'transparent',
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.sm,
    minHeight: 40,
  },
  
  // Sizes
  buttonSmall: {
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.md,
    minHeight: 40,
  },
  buttonLarge: {
    paddingVertical: Theme.spacing.xl,
    paddingHorizontal: Theme.spacing.xxl,
    minHeight: 64,
  },
  
  // States
  buttonDisabled: {
    opacity: 0.5,
    backgroundColor: '#d1d5db', // Gray color for disabled state
  },
  buttonPressed: {
    opacity: 0.8,
  },
  
  // Text styles
  text: {
    fontFamily: Theme.typography.fontFamily.medium,
    fontSize: 16,
    lineHeight: 24,
  } as TextStyle,
  textPrimary: {
    color: Theme.colors.textInverse,
  } as TextStyle,
  textSecondary: {
    color: Theme.colors.textPrimary,
  } as TextStyle,
  textOutline: {
    color: Theme.colors.primary,
  } as TextStyle,
  textLink: {
    color: Theme.colors.primary,
    fontFamily: Theme.typography.fontFamily.regular,
    textDecorationLine: 'underline',
  } as TextStyle,
  textSmall: {
    fontSize: 14,
    lineHeight: 20,
  } as TextStyle,
  textLarge: {
    fontSize: 18,
    lineHeight: 28,
  } as TextStyle,
  icon: {
    fontSize: 16,
  } as TextStyle,
}); 